import { useEffect, useRef, useState } from 'react';
import { faCircleInfo } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { faArrowLeft, faMagnifyingGlass } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';
import { Link } from '@inertiajs/react';

import { AddPageMenu } from '@/components/page-editor/add-page-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { TextInput } from '@/components/ui/form/text-input';
import { Icon } from '@/components/ui/icon';
import { IconTooltipButton } from '@/components/ui/icon-tooltip-button';
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenuSkeleton,
} from '@/components/ui/sidebar';
import { getHorizontalSlideClasses } from '@/components/ui/system/transitions';
import { Heading5 } from '@/components/ui/typography';
import { cmsRootPath } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { usePagesActions, usePagesState } from '@/stores/pages-store';
import type { Page } from '@/types/page';
import { PageTreeScopeList } from './page-tree-scope-list';
import { PageTypeView } from './page-type-view';
import { usePagePagination } from './use-page-pagination';
import { useTreeSearchResults } from './use-tree-search-results';

const MAIN_SECTION = 'main';
const NON_NAV_SECTION = 'non-nav';
const OPEN_NON_NAV_BELOW_COUNT = 25;

function PageTreeSidebar({ isActive }: { isActive: boolean }) {
  const { hasLoaded, mainNavigationTree, nonNavigationTree } = usePagesState();
  const { loadPages, reorderPage } = usePagesActions();

  const {
    activeNestedView,
    handleBackToPages,
    handlePageClick,
    handleLoadMorePages,
    handleSearchPages,
  } = usePagePagination({
    isSidebarActive: isActive,
    hasLoaded,
    mainNavigationTree,
    nonNavigationTree,
  });

  useEffect(() => {
    if (!isActive || hasLoaded) {
      return;
    }

    loadPages();
  }, [isActive, hasLoaded, loadPages]);

  return (
    <>
      <SidebarHeader className="mt-4 px-3">
        {activeNestedView ? (
          <Button
            variant="ghost"
            className="justify-start"
            colorScheme="secondary"
            onClick={handleBackToPages}
            aria-label="Back to Pages"
          >
            <Icon icon={faArrowLeft} />
            <span className="group-data-[collapsible=icon]:hidden">Pages</span>
          </Button>
        ) : (
          <Button asChild variant="ghost" colorScheme="secondary" className="justify-start">
            <Link href={cmsRootPath()} prefetch="mount" aria-label="Back to Dashboard">
              <Icon icon={faArrowLeft} />
              <span className="group-data-[collapsible=icon]:hidden">Dashboard</span>
            </Link>
          </Button>
        )}
      </SidebarHeader>
      <SidebarContent className="overflow-hidden">
        <div className="relative w-full h-full">
          <div
            className={getHorizontalSlideClasses(!activeNestedView, 'left')}
            inert={!!activeNestedView}
          >
            <div className="flex h-full min-h-0 flex-col">
              <SidebarHeader className="px-4 pb-0 flex flex-row items-center justify-between shrink-0">
                <Heading5>Pages</Heading5>
                <AddPageMenu />
              </SidebarHeader>
              <div className="flex min-h-0 flex-1 flex-col">
                <PageTreeContent
                  isActive={isActive && !activeNestedView}
                  onPageClick={handlePageClick}
                />
              </div>
            </div>
          </div>
          <div
            className={getHorizontalSlideClasses(!!activeNestedView, 'right')}
            inert={!activeNestedView}
          >
            <div className="flex h-full flex-col overflow-y-auto">
              {activeNestedView && (
                <PageTypeView
                  key={activeNestedView.pageId}
                  view={activeNestedView}
                  onLoadMore={activeNestedView.isIndexPage ? handleLoadMorePages : undefined}
                  onSearch={activeNestedView.isIndexPage ? handleSearchPages : undefined}
                  onReorder={reorderPage}
                />
              )}
            </div>
          </div>
        </div>
      </SidebarContent>
    </>
  );
}

interface PageTreeContentProps {
  isActive: boolean;
  onPageClick: (page: Page) => void;
}

function PageTreeContent({ isActive, onPageClick }: PageTreeContentProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [openSections, setOpenSections] = useState<string[]>([MAIN_SECTION]);
  const didApplyNonNavDefault = useRef(false);
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const nonNavScrollRef = useRef<HTMLDivElement>(null);
  const { hasLoaded, mainNavigationPagination } = usePagesState();
  const {
    navigationTree,
    nonNavigationTree,
    isLoading: isSearchLoading,
    isSearching,
  } = useTreeSearchResults(searchQuery);

  const isMainOpen = openSections.includes(MAIN_SECTION);
  const isNonNavOpen = openSections.includes(NON_NAV_SECTION);

  useEffect(() => {
    if (isActive) return;

    setSearchQuery('');
  }, [isActive]);

  useEffect(() => {
    if (!hasLoaded || didApplyNonNavDefault.current) return;
    didApplyNonNavDefault.current = true;
    if (mainNavigationPagination.totalCount >= OPEN_NON_NAV_BELOW_COUNT) return;

    setOpenSections([MAIN_SECTION, NON_NAV_SECTION]);
  }, [hasLoaded, mainNavigationPagination.totalCount]);

  useEffect(() => {
    if (!isSearching || nonNavigationTree.length === 0) return;

    setOpenSections((current) =>
      current.includes(NON_NAV_SECTION) ? current : [...current, NON_NAV_SECTION],
    );
  }, [isSearching, nonNavigationTree.length]);

  const resetTreeScroll = () => {
    mainScrollRef.current?.scrollTo(0, 0);
    nonNavScrollRef.current?.scrollTo(0, 0);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 px-4 py-3">
        <TextInput
          placeholder="Search pages..."
          leftSection={<Icon icon={faMagnifyingGlass} className="text-foreground" />}
          value={searchQuery}
          onChange={(event) => {
            setSearchQuery(event.target.value);
            resetTreeScroll();
          }}
          autoComplete="off"
        />
      </div>
      <Accordion
        type="multiple"
        value={openSections}
        onValueChange={setOpenSections}
        className="flex min-h-0 flex-1 flex-col px-2"
      >
        <AccordionItem
          value={MAIN_SECTION}
          className={cn('border-b-0', isMainOpen && 'flex min-h-0 flex-1 flex-col overflow-hidden')}
        >
          <AccordionTrigger className="shrink-0 items-center px-2 py-2 font-semibold [&>svg]:translate-y-0">
            Main Navigation
          </AccordionTrigger>
          <AccordionContent
            ref={mainScrollRef}
            className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto scroll-fade-y pb-0 data-[state=closed]:animate-none data-[state=open]:animate-none"
          >
            <PageTreeScopeList
              scope="navigation"
              onPageClick={onPageClick}
              searchQuery={searchQuery}
              pages={navigationTree}
              isSearching={isSearching}
              isSearchLoading={isSearchLoading}
              isActive={isActive}
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem
          value={NON_NAV_SECTION}
          className={cn(
            'border-t border-b-0 bg-sidebar',
            isNonNavOpen && !isMainOpen
              ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
              : 'shrink-0',
          )}
        >
          <AccordionTrigger className="shrink-0 items-center px-2 py-2 font-semibold [&>svg]:translate-y-0">
            <div className="flex items-center gap-1">
              <span>Non-Navigation Pages</span>
              <IconTooltipButton
                as="span"
                size="icon-sm"
                tooltip="Non-navigation pages are publicly accessible but will not appear in your navigation menu."
                tooltipContentProps={{ className: 'max-w-60 text-center text-pretty' }}
              >
                <Icon icon={faCircleInfo} className="size-4 text-muted-foreground" />
              </IconTooltipButton>
            </div>
          </AccordionTrigger>
          <AccordionContent
            ref={nonNavScrollRef}
            className={cn(
              'overflow-x-hidden overflow-y-auto scroll-fade-y pb-0 data-[state=closed]:animate-none data-[state=open]:animate-none',
              isMainOpen ? 'h-[30vh]' : 'min-h-0 flex-1',
            )}
          >
            <PageTreeScopeList
              scope="non_navigation"
              onPageClick={onPageClick}
              searchQuery={searchQuery}
              pages={nonNavigationTree}
              isSearching={isSearching}
              isSearchLoading={isSearchLoading}
              isActive={isActive}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

export function PageTreeSkeleton() {
  const skeletons = Array.from({ length: 6 }, (_, i) => `skeleton-item-${i}`);

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        {skeletons.map((skeleton) => (
          <SidebarMenuSkeleton key={skeleton} showIcon className="px-3 py-2" />
        ))}
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

export default PageTreeSidebar;
